<script lang="ts">
  import { onMount } from 'svelte';
  import type { ApiHealth, ApiMeta, DeviceSummary, TenantSummary } from '@ucc/shared';
  import { fetchDevices, fetchHealth, fetchMeta, fetchTenants } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let health = $state<ApiHealth | null>(null);
  let meta = $state<ApiMeta | null>(null);
  let devices = $state<DeviceSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let error = $state<string | null>(null);
  let loading = $state(true);

  onMount(async () => {
    try {
      const [h, m, d, t] = await Promise.all([
        fetchHealth(),
        fetchMeta(),
        fetchDevices(),
        fetchTenants(),
      ]);
      health = h;
      meta = m;
      devices = d.devices;
      tenants = t.tenants;
    } catch (e) {
      error = e instanceof Error ? e.message : 'API unreachable';
    } finally {
      loading = false;
    }
  });

  const onlineCount = $derived(devices.filter((d) => d.status === 'online').length);

  const kpis = $derived([
    {
      label: 'Devices online',
      value: loading ? '…' : String(onlineCount),
      hint: loading ? 'Loading' : `${devices.length} visible to you`,
      tone: 'accent' as const,
    },
    {
      label: 'Tenants in scope',
      value: loading ? '…' : String(tenants.length),
      hint: session.role === 'admin' ? 'Platform-wide' : session.tenantName || 'Your tenant',
      tone: 'neutral' as const,
    },
    {
      label: 'Signed in as',
      value: session.user?.role ?? '—',
      hint: session.email,
      tone: 'warning' as const,
    },
    {
      label: 'API',
      value: health?.status ?? (loading ? '…' : 'down'),
      hint: health?.time ? new Date(health.time).toLocaleString() : error ?? 'Waiting',
      tone: (health?.status === 'ok' ? 'success' : error ? 'danger' : 'neutral') as
        | 'success'
        | 'danger'
        | 'neutral',
    },
  ]);
</script>

<div class="page stack">
  <section class="hero">
    <div>
      <p class="eyebrow">Signed in · {session.tenantName}</p>
      <h2>Welcome, {session.displayName}</h2>
      <p class="lede secondary">
        Cookie session auth is live for local MVP. Role-aware shell for <strong>Admin</strong>,
        staff <strong>User</strong>, and tenant <strong>Client</strong>. Protected device/tenant
        stubs power the KPIs below.
      </p>
    </div>
    <div class="hero-actions">
      <Button variant="primary">Add device</Button>
      <Button variant="secondary">Import group</Button>
    </div>
  </section>

  <section class="kpi-grid">
    {#each kpis as kpi (kpi.label)}
      <Card>
        <p class="kpi-label muted">{kpi.label}</p>
        <p class="kpi-value">{kpi.value}</p>
        <div class="kpi-foot">
          <Badge tone={kpi.tone}>{kpi.hint}</Badge>
        </div>
      </Card>
    {/each}
  </section>

  <section class="split">
    <Card title="Portal scope" subtitle="What this role can reach in MVP1">
      <ul class="scope-list">
        {#if session.role === 'admin'}
          <li>Create/disable staff users and clients</li>
          <li>Assign devices and groups across tenants</li>
          <li>Full audit, OTA, and platform settings</li>
        {:else if session.role === 'user'}
          <li>Operate assigned devices and groups</li>
          <li>Run OTA within policy</li>
          <li>No staff/client directory admin</li>
        {:else}
          <li>View assigned tenant devices and groups</li>
          <li>Read-focused telemetry and status</li>
          <li>No staff, audit, or global OTA controls</li>
        {/if}
      </ul>
    </Card>

    <Card title="Stack pulse" subtitle="Local scaffold health">
      <dl class="meta">
        <div>
          <dt>Product</dt>
          <dd>{meta?.name ?? 'Unarvu Cloud Connect'}</dd>
        </div>
        <div>
          <dt>API service</dt>
          <dd>{health?.service ?? 'ucc-api'}</dd>
        </div>
        <div>
          <dt>Version</dt>
          <dd>{meta?.version ?? health?.version ?? '0.2.0'}</dd>
        </div>
        <div>
          <dt>Auth</dt>
          <dd>httpOnly cookie · {session.user?.role ?? '—'}</dd>
        </div>
        <div>
          <dt>Auth store</dt>
          <dd>{meta?.authStore ?? '—'}</dd>
        </div>
        <div>
          <dt>Env</dt>
          <dd>{meta?.env ?? 'development'}</dd>
        </div>
      </dl>
      {#if error}
        <p class="api-error">
          API not reached ({error}). Run <code>pnpm dev</code> so web + api start together.
        </p>
      {/if}
    </Card>
  </section>

  <Card title="Scalable UI map" subtitle="How the front end grows without rewrites">
    <div class="map-grid">
      <div>
        <h4>Tokens</h4>
        <p class="secondary">Color, type, space, elevation in CSS variables — theme-ready.</p>
      </div>
      <div>
        <h4>Primitives</h4>
        <p class="secondary">Button, Badge, Card, Icon — compose pages, avoid one-off CSS.</p>
      </div>
      <div>
        <h4>Shell</h4>
        <p class="secondary">Role-filtered nav + topbar; pages stay route modules.</p>
      </div>
      <div>
        <h4>Contracts</h4>
        <p class="secondary"><code>@ucc/shared</code> types shared with the API.</p>
      </div>
    </div>
  </Card>
</div>

<style>
  .hero {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--space-5);
    align-items: flex-end;
    padding: var(--space-6);
    border: 1px solid var(--border);
    border-radius: var(--radius-xl);
    background:
      linear-gradient(135deg, color-mix(in srgb, var(--brand-500) 16%, transparent), transparent 55%),
      color-mix(in srgb, var(--bg-elevated) 90%, transparent);
  }

  .eyebrow {
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--accent-strong);
    font-weight: var(--weight-semibold);
    margin-bottom: var(--space-2);
  }

  .hero h2 {
    font-size: clamp(1.4rem, 2vw, 1.85rem);
    max-width: 22ch;
  }

  .lede {
    margin-top: var(--space-3);
    max-width: 52ch;
    font-size: var(--text-md);
  }

  .lede strong {
    color: var(--text);
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-4);
  }

  .kpi-label {
    font-size: var(--text-sm);
    margin-bottom: var(--space-2);
  }

  .kpi-value {
    font-size: var(--text-3xl);
    font-weight: var(--weight-semibold);
    letter-spacing: -0.03em;
    margin-bottom: var(--space-3);
  }

  .kpi-foot {
    display: flex;
  }

  .split {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: var(--space-4);
  }

  .scope-list {
    display: grid;
    gap: var(--space-3);
  }

  .scope-list li {
    position: relative;
    padding-left: var(--space-5);
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }

  .scope-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.45rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-muted);
  }

  .meta {
    display: grid;
    gap: var(--space-3);
  }

  .meta div {
    display: grid;
    grid-template-columns: 7rem 1fr;
    gap: var(--space-3);
    font-size: var(--text-sm);
  }

  .meta dt {
    color: var(--text-muted);
  }

  .meta dd {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
  }

  .api-error {
    margin-top: var(--space-4);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--danger-muted);
    color: var(--danger);
    font-size: var(--text-sm);
  }

  .api-error code {
    font-size: var(--text-xs);
  }

  .map-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-4);
  }

  .map-grid h4 {
    font-size: var(--text-sm);
    margin-bottom: var(--space-2);
  }

  .map-grid p {
    font-size: var(--text-sm);
  }

  .map-grid code {
    font-size: var(--text-xs);
    color: var(--accent-strong);
  }

  @media (max-width: 1100px) {
    .kpi-grid,
    .map-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .split {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    .kpi-grid,
    .map-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
